<template>
  <v-container :class="containerClass" fluid>
    <v-card flat>
      <v-card-title>
        <div class="d-flex align-center">
          <v-btn icon color="primary" left :href="$route('classrooms.index')">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <h2>{{ classroom.data.name }}</h2>
        </div>
      </v-card-title>
      <v-card-text>
        <v-container fluid>
          <v-row>
            <v-col cols="12" md="6">
              <v-card class="mb-4">
                <v-card-title>Class Info</v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" md="4">
                        <h3>Class Date</h3>
                        <div>
                          {{
                            formatDate(classroom.data.start_date, 'D MMM YYYY')
                          }}
                          -
                          {{
                            formatDate(classroom.data.end_date, 'D MMM YYYY')
                          }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="4">
                        <h3>Class Type</h3>
                        <div>
                          {{
                            classroomTypeProps(
                              classroom.data.class_type,
                              'text'
                            )
                          }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="4">
                        <h3>Status</h3>
                        <div>
                          <v-chip
                            :color="
                              classroomStatusProps(
                                classroom.data.status,
                                'color'
                              )
                            "
                            small
                          >
                            {{
                              classroomStatusProps(
                                classroom.data.status,
                                'text'
                              )
                            }}
                          </v-chip>
                        </div>
                      </v-col>
                      <v-col cols="12" md="4">
                        <h3>Teacher</h3>
                        <div>{{ classroom.data.teacher.name }}</div>
                      </v-col>
                      <v-col cols="12" md="4">
                        <h3>Lesson Plan</h3>
                        <div>
                          {{
                            classroom.data.subscription_package.lesson_plan.name
                          }}
                        </div>
                      </v-col>
                      <v-col cols="12" md="4">
                        <h3>Online Class URL</h3>
                        <div>
                          <a
                            v-if="classroom.data.online_class_url"
                            :href="classroom.data.online_class_url"
                          >
                            Open online class here
                          </a>
                          <span v-else>-</span>
                        </div>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <h3>Weekly Schedules</h3>
                        <v-list>
                          <template
                            v-for="weeklySchedule in classroom.data
                              .weekly_schedules"
                          >
                            <v-list-item :key="weeklySchedule.id">
                              <v-container fluid class="pa-0">
                                <v-row align="center" dense>
                                  <v-col class="font-weight-bold">
                                    {{
                                      dayOfWeekProps(
                                        weeklySchedule.day_of_week,
                                        'text'
                                      )
                                    }}
                                  </v-col>
                                  <v-col>
                                    <v-icon
                                      v-if="!$vuetify.breakpoint.mobile"
                                      left
                                      small
                                    >
                                      mdi-clock-outline
                                    </v-icon>
                                    {{ formatTime(weeklySchedule.start_time) }}
                                    -
                                    {{ formatTime(weeklySchedule.end_time) }}
                                  </v-col>
                                </v-row>
                              </v-container>
                            </v-list-item>
                          </template>
                        </v-list>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card>
                <v-card-title>Recent Class Schedules</v-card-title>
                <v-divider></v-divider>
                <v-card-text>
                  <template v-for="(classSchedule, i) in recentClassSchedules">
                    <v-list :key="i" subheader>
                      <v-subheader>
                        <h3>{{ classSchedule.title }}</h3>
                      </v-subheader>
                      <v-list-item
                        v-for="item in classSchedule.items"
                        :key="item.class_date"
                      >
                        <v-list-item-content>
                          <v-container fluid class="py-0">
                            <v-row>
                              <v-col cols="12" md="10">
                                <v-row align="center">
                                  <v-col cols="6" class="font-weight-bold">
                                    {{
                                      formatDate(item.class_date, 'D MMM YYYY')
                                    }}
                                    <v-chip
                                      v-if="!$vuetify.breakpoint.mobile"
                                      small
                                      class="mx-1"
                                    >
                                      {{ timeFromNow(item.class_date) }}
                                    </v-chip>
                                  </v-col>
                                  <v-col
                                    cols="6"
                                    :class="{
                                      'text-right': $vuetify.breakpoint.mobile,
                                    }"
                                  >
                                    <v-icon
                                      v-if="!$vuetify.breakpoint.mobile"
                                      left
                                      small
                                    >
                                      mdi-clock-outline
                                    </v-icon>
                                    {{ formatTime(item.start_time) }} -
                                    {{ formatTime(item.end_time) }}
                                  </v-col>
                                  <v-col cols="12">
                                    <div class="mb-2">Lessons:</div>
                                    <ol>
                                      <li
                                        v-for="(lesson, j) in item.lessons"
                                        :key="j"
                                      >
                                        {{
                                          bilingualText(
                                            lesson,
                                            'name',
                                            'name_cn'
                                          )
                                        }}
                                      </li>
                                    </ol>
                                  </v-col>
                                </v-row>
                              </v-col>
                              <v-col cols="12" md="2" class="text-right">
                                <v-btn
                                  text
                                  small
                                  color="info"
                                  :href="
                                    $route(
                                      'classroom-schedules.update',
                                      item.id
                                    )
                                  "
                                >
                                  See Details
                                </v-btn>
                              </v-col>
                            </v-row>
                          </v-container>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                    <v-divider
                      v-if="i < recentClassSchedules.length - 1"
                      :key="`div-${i}`"
                    ></v-divider>
                  </template>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <calendar-view
                :schedules="schedules"
                :loading="isScheduleListLoading"
              >
              </calendar-view>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-container>
</template>
<script>
import LayoutDashboard from '@layouts/Dashboard'
import CalendarView from '@components/ClassroomSchedule/CalendarView'
import { mapActions, mapGetters } from 'vuex'

const thisWeekStart = moment().startOf('week')
const thisWeekEnd = moment().endOf('week')
export default {
  name: 'StudentClassroomDetail',
  components: {
    CalendarView,
  },
  layout: LayoutDashboard,
  props: {
    classroom: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      panels: [0, 1, 2],
    }
  },
  computed: {
    ...mapGetters({
      scheduleList: 'classroomSchedule/list',
      isScheduleListLoading: 'classroomSchedule/isListLoading',
    }),
    schedules() {
      return this.scheduleList.data
    },
    recentClassSchedules() {
      return [
        {
          title: 'This Week',
          items: this.thisWeekSchedules,
        },
        {
          title: 'Last Week',
          items: this.lastWeekSchedules,
        },
        {
          title: 'Next Week',
          items: this.nextWeekSchedules,
        },
      ]
    },
    thisWeekSchedules() {
      return this.schedules.filter((e) => {
        const dt = moment(e.class_date)
        return dt.isSameOrAfter(thisWeekStart) && dt.isSameOrBefore(thisWeekEnd)
      })
    },
    lastWeekSchedules() {
      const lastWeekStart = moment(thisWeekStart).subtract(1, 'weeks')
      const lastWeekEnd = moment(thisWeekEnd).subtract(1, 'weeks')
      return this.schedules.filter((e) => {
        const dt = moment(e.class_date)
        return dt.isSameOrAfter(lastWeekStart) && dt.isSameOrBefore(lastWeekEnd)
      })
    },
    nextWeekSchedules() {
      const nextWeekStart = moment(thisWeekStart).add(1, 'weeks')
      const nextWeekEnd = moment(thisWeekEnd).add(1, 'weeks')
      return this.schedules.filter((e) => {
        const dt = moment(e.class_date)
        return dt.isSameOrAfter(nextWeekStart) && dt.isSameOrBefore(nextWeekEnd)
      })
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({
      getScheduleList: 'classroomSchedule/getList',
    }),
    async init() {
      await this.getScheduleList({
        classroom_id: this.classroom.data.id,
      })
    },
  },
}
</script>
